import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { replaceIconsWithTickItems } from '@theme/quick-modal-tick-items';
import {
  QuickModalJuoState,
  handleModalJuoVariantUpdate,
  isolatePageJuoForModal,
  restorePageIsolation,
} from '@theme/quick-modal-juo';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Document | null>>} */
  #inflightFetches = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {QuickModalJuoState} */
  #juoState = new QuickModalJuoState();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    this.#inflightFetches.clear();
    this.#abortController?.abort();
    restorePageIsolation(this.#juoState);
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    try {
      // Check if we have cached content for this URL
      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }

      if (!productGrid) return;

      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
      this.#openQuickAddModal();
    } catch (error) {
      if (error?.name !== 'AbortError') {
        throw error;
      }
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const modalContent = dialogComponent.querySelector('.quick-add-modal__content');
    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');

    modalContent?.scrollTo({ top: 0, behavior: 'instant' });
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute('stay-visible', false);
        restorePageIsolation(this.#juoState);
      },
      { once: true }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  fetchProductPage(productPageUrl) {
    if (!productPageUrl) return Promise.resolve(null);

    const inflightRequest = this.#inflightFetches.get(productPageUrl);
    if (inflightRequest) return inflightRequest;

    const fetchPromise = this.#fetchProductPage(productPageUrl);
    this.#inflightFetches.set(productPageUrl, fetchPromise);

    return fetchPromise.finally(() => {
      if (this.#inflightFetches.get(productPageUrl) === fetchPromise) {
        this.#inflightFetches.delete(productPageUrl);
      }
    });
  }

  /**
   * Performs the product page fetch.
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async #fetchProductPage(productPageUrl) {
    // Abort only a previous in-flight request for a different URL.
    this.#abortController?.abort();
    const requestController = new AbortController();
    this.#abortController = requestController;

    try {
      const response = await fetch(productPageUrl, {
        signal: requestController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error?.name === 'AbortError') {
        return null;
      }

      throw error;
    } finally {
      if (this.#abortController === requestController) {
        this.#abortController = null;
      }
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.layoutQuickAddModalContent(modalContent);
    isolatePageJuoForModal(modalContent, this.#juoState);
  }

  /**
   * Applies quick-add modal layout (price in button, variant label copy).
   * @param {Element} [modalContent] - The modal content element
   */
  layoutQuickAddModalContent(modalContent = document.getElementById('quick-add-modal-content')) {
    if (!modalContent) return;

    this.#layoutQuickAddModalContent(modalContent);
    this.#customizeQuickAddModal(modalContent);
    this.#enableModalMediaScroll(modalContent);
  }

  /**
   * Enable slideshow scrolling inside the dialog (IntersectionObserver may miss nested dialogs).
   * @param {Element} modalContent
   */
  #enableModalMediaScroll(modalContent) {
    modalContent.querySelectorAll('slideshow-component').forEach((slideshow) => {
      slideshow.setAttribute('in-viewport', '');
    });
  }

  /**
   * Customizes variant picker copy for the quick-add modal.
   * @param {Element} modalContent - The modal content element
   */
  #customizeQuickAddModal(modalContent) {
    replaceIconsWithTickItems(modalContent);

    const legend = modalContent.querySelector('.variant-option--buttons legend');

    if (!legend) return;

    const optionName = legend.textContent.trim();

    if (/^size$/i.test(optionName)) {
      legend.textContent = 'Choose a size:';
    }
  }

  /**
   * Moves the product price into the add-to-cart button for the quick-add layout.
   * @param {Element} modalContent - The modal content element
   */
  #layoutQuickAddModalContent(modalContent) {
    const buyButtons = modalContent.querySelector('.buy-buttons-block');
    const addToCartText = buyButtons?.querySelector('.add-to-cart-button .add-to-cart-text');
    const price =
      modalContent.querySelector('.quick-add-modal__price product-price') ??
      modalContent.querySelector('.product-details product-price') ??
      modalContent.querySelector('product-price');

    if (!buyButtons || !addToCartText || !price) return;

    let priceSlot = addToCartText.querySelector('.quick-add-modal__price');

    if (!priceSlot) {
      priceSlot = document.createElement('span');
      priceSlot.className = 'quick-add-modal__price';
      addToCartText.appendChild(priceSlot);
    }

    if (!priceSlot.contains(price)) {
      priceSlot.appendChild(price);
    }

    if (price instanceof HTMLElement && price.dataset.blockId) {
      priceSlot.dataset.priceBlockId = price.dataset.blockId;
    }
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  /**
   * Re-applies modal customizations after a variant change.
   * @param {import('./events').VariantUpdateEvent} [event]
   */
  handleModalVariantUpdate(event) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    this.#layoutQuickAddModalContent(modalContent);
    replaceIconsWithTickItems(modalContent);

    if (event) {
      const modalProductPrice = modalContent.querySelector('.quick-add-modal__price product-price');
      if (modalProductPrice && typeof modalProductPrice.updatePrice === 'function') {
        modalProductPrice.updatePrice(event);
      }
    }

    handleModalJuoVariantUpdate(modalContent, this.#juoState, '.quick-add-modal__price product-price');
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleVariantUpdate = (/** @type {CustomEvent} */ event) => {
    const quickAddComponent = document.querySelector('quick-add-component[stay-visible]');

    if (!(quickAddComponent instanceof QuickAddComponent)) return;

    // Run after product-form morph re-attaches the modal price slot.
    queueMicrotask(() => {
      quickAddComponent.handleModalVariantUpdate(event);
    });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
